import { Plugin, Context } from '@nuxt/types';
import { Location } from 'vue-router';

import { useApi } from './apis';
// import { AppLocation } from './location';
import { useLocation } from './locations';
import { globalState } from './globalState';

import { StorageName } from '~/components/settings/Timeline';
import { UserRole } from '~/components/settings/User';
import { removeTimelineToStorage } from '~/components/tool/Timeline';
import {
  isWellnessCloudEnabled,
  toDelegatedCardType,
  toReturnToPath,
  toWellnessCloudScreen,
  toWellnessCloudSurveyUrl,
} from '~/utils/wellnesscloud';
import {
  consumeWellnessCloudUserMismatch,
  resumeWellnessCloudRefresh,
  waitForWellnessCloudRefresh,
} from '~/utils/wellnesscloudSession';

// 委譲先のトークン更新中に離れると、ローテーション済みのトークンを取りこぼして委譲先の系統ごと失効する。待ちすぎると問診が開かないため、上限を設ける
const REFRESH_WAIT_BEFORE_DELEGATION_MS = 5 * 1000;

// beforeEachは$auth0の初期化前にも走るため、未初期化ならログイン時に保存したroleで判定する。
const isPatient = (app: Context['app']): boolean => {
  const role = (app as any).$auth0?.user?.role || localStorage.getItem('role');
  return role === UserRole.Patient;
};

const plugin: Plugin = async ({ app }, inject): Promise<void> => {
  removeTimelineToStorage(StorageName.Timeline);
  removeTimelineToStorage(StorageName.UserTimeline);

  inject('api', useApi(app.$axios));
  // inject('location', new AppLocation());
  inject('location', useLocation());

  const $s = globalState(app as Context);
  inject('s', $s);

  let navigationCount = 0;

  if (app.router) {
    app.router.beforeEach(async (to, from, next): Promise<void> => {
      navigationCount += 1;
      const navigationId = navigationCount;
      $s.setToPage(to as Location);
      $s.setFromPage(from as Location);

      // 委譲するのは従業員(patient)だけ。医療者の回答結果閲覧は本アプリのまま。
      // 本ルートは着地しても画面を描画しないため、replaceで履歴に残さない。
      // assignだと「戻る」が空画面の本ルートに戻り、ホームまで2回押すことになる。
      const screen = toWellnessCloudScreen(to);
      if (screen && isWellnessCloudEnabled() && isPatient(app)) {
        const projectId = String(to.params.projectId);
        const questionnaireId = String(to.params.questionnaireId);
        const returnTo = toReturnToPath(from, to);
        const cardType = toDelegatedCardType(to);

        await waitForWellnessCloudRefresh(REFRESH_WAIT_BEFORE_DELEGATION_MS);
        // 待機中に別の遷移が始まっていれば委譲しない。vue-routerはnextが呼ばれるまでこのガードを取り消せず、
        // 後の遷移が完了した後で問診へ飛んでしまうため
        if (navigationId !== navigationCount) {
          return;
        }
        resumeWellnessCloudRefresh();

        // next(false)はvue-routerがensureURL(true)で履歴をpushするため呼ばない。
        // pushが先に効くとreplaceがそちらを差し替え、本ルートが履歴に残る。
        // ナビゲーションは保留のままにし、遷移はブラウザに任せる。
        const { $auth0 } = app as any;
        if ($auth0 && consumeWellnessCloudUserMismatch()) {
          // 委譲先が別ユーザーとして動いている。SSOセッションもそのユーザーのため、委譲先で認証し直しても入れ替わらない。
          // Auth0と委譲先の両方を経由する本アプリのログアウトで、すべて消してからログインし直させる
          $auth0.logout({ returnTo: window.location.origin });
          return;
        }
        window.location.replace(toWellnessCloudSurveyUrl({
          screen,
          projectId,
          questionnaireId,
          returnTo,
          cardType,
        }));
        return;
      }

      next();
    });
  }
};

export default plugin;
